import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative flex min-h-screen items-center justify-center bg-petrol px-5"
      data-testid="admin-login-page"
    >
      <div className="glow-brand-strong pointer-events-none absolute -top-32 left-1/3 h-96 w-96 rounded-full" />
      <div className="relative w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Logo dark dataTestId="admin-login-logo" />
        </div>
        <form
          onSubmit={submit}
          className="rounded-3xl border border-white/10 bg-white p-8 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)] sm:p-10"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand">
              <Lock className="h-4.5 w-4.5" />
            </span>
            <div>
              <h1 className="font-display text-xl font-bold text-petrol">Painel Resolv</h1>
              <p className="text-xs text-slate-500">Acesso restrito à equipe</p>
            </div>
          </div>

          <div className="mt-7 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="admin-email">E-mail</Label>
              <Input
                id="admin-email"
                data-testid="admin-login-email-input"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="admin-password">Senha</Label>
              <Input
                id="admin-password"
                data-testid="admin-login-password-input"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          {error && (
            <p
              className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-600"
              data-testid="admin-login-error"
              role="alert"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            className="mt-6 h-12 w-full rounded-full bg-petrol font-semibold text-white transition-colors hover:bg-brand"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar no painel"}
          </Button>
        </form>
        <p className="mt-6 text-center">
          <Link
            to="/"
            data-testid="admin-login-back-link"
            className="text-sm text-white/50 transition-colors hover:text-white"
          >
            ← Voltar para o site
          </Link>
        </p>
      </div>
    </div>
  );
}
